/**
 * curlFlow — warp-field preset using a curl-noise displacement field.
 *
 * The curl of a 2D noise potential is divergence-free, so the resulting
 * flow doesn't pile up at any point. Visually: smooth, organic swirling
 * that doesn't feel mechanical. Calm cooldown (~1.6s) so adoption only
 * lands on confident beats, and a long crossfade (~600ms) so the field
 * change is felt as a slow morph rather than a swap.
 */

import { WarpKind, type VisualizerPreset } from '../presetTypes';

export const curlFlowPreset: VisualizerPreset = {
  id: 'curlFlow',
  name: 'Curl Flow',
  seed: {
    kind: 'waveform',
    params: {
      baseY: 0.0,
      gain: 0.42,
      halfThick: 0.012,
      intensity: 0.62,
      color: [0.55, 0.95, 0.7], // soft mint
      edgeSharp: 9.5,
    },
  },
  warp: {
    kind: WarpKind.Field,
    params: {
      // baseZoom still applies under the field path -- it's the underlying
      // tunnel pull. The displacement from the field is added on top.
      baseZoom: 0.008,
      bassZoomGain: 0.03,
      rotation: 0,
      bassRotationGain: 0,
      ripple: 0,
      rippleFreq: 0,
      decay: 0.975,
      chroma: 0.0028,
      hueShift: 0.012,
      beatPunch: 0.05,
      centerDriftAmp: 0.06,
      centerDriftRate: 0.11,
    },
  },
  warpField: {
    generator: 'curl',
    seed: 0.31,
    strength: 0.014,
    centerX: 0.5,
    centerY: 0.5,
    // For curl, scale (param0) controls noise frequency; bigger = tighter swirls.
    param0: 3.5,
    param1: 0,
    param2: 0,
    adoption: {
      beatThreshold: 0.55,
      cooldownMs: 1600,
      crossfadeMs: 600,
      regenerateOnAdopt: true,
    },
  },
  modulation: [
    {
      target: 'warp.baseZoom',
      source: 'bass',
      transform: 'smoothPulse',
      amount: 0.012,
      smoothingMs: 110,
    },
    {
      target: 'seed.intensity',
      source: 'rms',
      transform: 'linear',
      amount: 0.4,
      smoothingMs: 70,
    },
    {
      target: 'warp.hueShift',
      source: 'beatConfidence',
      transform: 'smoothPulse',
      amount: 0.025,
      smoothingMs: 250,
    },
  ],
  transition: { durationMs: 1000 },
  present: {
    bloomEnabled: true,
    // Curl flow has subtle highlights -- a low threshold catches them; a
    // wider spread matches the soft, organic motion.
    bloomThreshold: 0.55,
    bloomKnee: 0.22,
    bloomSpread: 1.4,
    bloomIntensity: 0.75,
    vignette: 0.22,
    grain: 0.012,
  },
};
